import { faDollarSign, faPercent } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dialog, DialogActions, DialogContent, dialogContentClasses, DialogProps, dialogTitleClasses, Divider, Grid, GridProps, InputAdornment, styled } from "@mui/material";
import { FormSubmitButton, IInputOnlyFieldProps, InputOnlyField, Numeric } from "@ngt/forms";
import { InputComponentType, OmitInputProps, useScopedField } from "@ngt/forms-core";
import { ComponentProps, FC } from "react";

interface IInputProps {
    label?: string;
    propertyPath: string;
    disabled?: boolean
}

export const DollarInput: FC<IInputProps> = ({
    label,
    propertyPath,
    disabled
}) => {
    const { state: { value, focused } } = useScopedField(propertyPath, { value: true, focused: true })
    
    return (
        <InputOnlyField 
            disabled={disabled}
            label={label}
            component={Numeric}
            propertyPath={propertyPath}
            numberFormat={{
                fixedDecimalScale: true,
                decimalScale: 2
            }}
            slotProps={{
                input: {
                    startAdornment: (value !== undefined && value !== null) || focused ? 
                        (
                            <InputAdornment position='start' >
                                <FontAwesomeIcon icon={faDollarSign} fixedWidth />
                            </InputAdornment>
                        ) :
                        undefined
                }
            }}
        />
    )
}

export const PercentInput: FC<IInputProps> = (props) => {
    return (
        <InputOnlyField
            component={Numeric}
            numberFormat={{
                min: 0,
                max: 100,
                isAllowed: (val) => {
                    if (!val.floatValue) {
                        return true;
                    }

                    if (val.floatValue < 0 || val.floatValue > 100) {
                        return false;
                    }

                    return true;
                }
            }}
            slotProps={{
                input: {
                    endAdornment: <InputAdornment position="end"><FontAwesomeIcon icon={faPercent}/></InputAdornment>
                }
            }}
            {...props}
        />
    );
}

export const FormDialog: FC<IFormDialogProps> = (props) => {
    return (
        <Dialog
            maxWidth="lg" 
            scroll="paper"
            fullWidth        
            {...props} 
            onClose={(_e, reason) => {
                if (reason === 'backdropClick' && props.disableOverlayClick) {
                    return;
                }

                if (reason === 'escapeKeyDown' && props.disableEscapeKeyDown) {
                    return;
                }

                props.onClose()
            }}
        />
    )
}

export const StyledDialog = styled(FormDialog) (({ theme }) => ({
    width: "100%",

    [`& .${dialogTitleClasses.root} + .${dialogContentClasses.root}`]: {
        padding: theme.spacing(3)
    }
}));

export const StyledDivider = styled(Divider) (({theme}) => ({
    margin: theme.spacing(3, -3)
}));

export const FormSection:FC<GridProps> = (props: GridProps) => {
    return (
        <Grid 
            container
            spacing={3}
            {...props}
        />
    );
}

export const StyledDialogContent = styled(DialogContent) (() => ({
}));

const BUTTON_WIDTH = "150px";

export const StyledDialogActions = styled(DialogActions) (({ theme }) => ({
    padding: theme.spacing(3),
    gap: theme.spacing(3),
    justifyContent: 'center'
}));

export const StyledFormColumn = styled(Grid) (({ theme }) => ({
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    flexDirection: "column",
    gap: theme.spacing(3),
}));

export const StyledButton = styled(Button) (() => ({
    width: BUTTON_WIDTH
}));

export const StyledFormSubmitButton = styled(FormSubmitButton) (() => ({
    width: BUTTON_WIDTH
}));

export interface IFormDialogProps extends Omit<DialogProps, 'onClose'> { 
    edit?: boolean;
    onClose: () => void;
    disableOverlayClick?: boolean;
    disableEscapeKeyDown?: boolean;
}

export const FixedHeightInputOnlyField: FC<IInputOnlyFieldProps & Partial<OmitInputProps<ComponentProps<InputComponentType>>>> = (props) => {
    return (
        <Grid
            sx={{
                display: "flex",
                alignItems: "center",
                height: "56px"
            }}
        >
            <InputOnlyField {...props}/>
        </Grid>
    );
}
// export default StyledDialog;